import React, { useEffect, useState, useCallback } from "react";
import { Box, Stack } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import type { ProfileBody, Section } from "../../types";
import TaxInfoPreview from "./TaxInfoPreview";
import TaxInfoReview from "./TaxInfoReview";
import { useProfileStore } from "../../store/profileStore";
import { TaxInfoSidebar } from "./TaxInfoSidebar";

function extractContentSections(node: ProfileBody | undefined): Section[] {
  if (!node || typeof node !== "object") return [];

  const topLevelKeys = [
    "mainInfo",
    "incomeSources",
    "business",
    "organizerBusinesses",
    "creditsAndDeductions",
    "additionalQuestions",
    "comments",
    "wagesAndTaxes",
  ];

  function hasValue(obj: any): boolean {
    if (!obj || typeof obj !== "object") return false;

    if ("value" in obj && obj.value !== null && obj.value !== "") {
      return true;
    }

    if (Array.isArray(obj)) {
      return obj.some(hasValue);
    }

    if ("fields" in obj && typeof obj.fields === "object") {
      return Object.values(obj.fields).some(hasValue);
    }

    if ("items" in obj && Array.isArray(obj.items)) {
      return obj.items.some(hasValue);
    }

    return Object.values(obj).some(hasValue);
  }

  return Object.entries(node)
    .filter(
      ([key, value]) =>
        topLevelKeys.includes(key) && value?.label && hasValue(value)
    )
    .map(([key, value]) => ({
      key,
      label: value!.label,
    }));
}

const TaxInfo: React.FC = () => {
  const [contentSections, setContentSections] = useState<Section[]>([]);
  const { review, contentData, sidebarCollapsed, setSelectedDoc } =
    useProfileStore();

  // This will hold a ref for each content section you want to scroll to:
  const [sectionsRefs, setSectionsRefs] = useState<
    React.RefObject<HTMLDivElement>[]
  >([]);

  const isMobile = useMediaQuery("(max-width: 768px)");

  // Build your content sections for sidebar from `contentData`
  useEffect(() => {
    setContentSections(
      contentData ? extractContentSections(contentData as ProfileBody) : []
    );
  }, [contentData]);

  useEffect(() => {
    if (sectionsRefs.length !== contentSections.length) {
      setSectionsRefs(
        contentSections.map(() => React.createRef<HTMLDivElement>())
      );
    }
  }, [contentSections, sectionsRefs]);

  // Throttled scroll function to prevent excessive calls during rapid navigation
  const scrollToSection = useCallback(
    (sectionIndex: number): void => {
      if (sectionsRefs[sectionIndex]?.current) {
        // Use requestAnimationFrame for smoother scrolling performance
        requestAnimationFrame(() => {
          sectionsRefs[sectionIndex]?.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      }
    },
    [sectionsRefs]
  );

  const renderBody = (): JSX.Element => {
    if (review) {
      return (
        <Stack w={"100%"} gap={0} data-tooltip-id="main-info">
          <div
            data-tooltip-id="edit-mode-banner"
            style={{
              background: "#0077CC",
              color: "#fff",
              padding: "10px",
              textAlign: "center",
            }}
          >
            You're in Edit Mode. Please make sure you review the data and make
            any needed changes before exporting. Always check the magic! 🪄
          </div>
          <TaxInfoReview
            contentData={contentData}
            sections={contentSections}
            sectionsRefs={sectionsRefs}
          />
        </Stack>
      );
    } else {
      return (
        <TaxInfoPreview
          sections={contentSections}
          sectionsRefs={sectionsRefs}
        />
      );
    }
  };

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        width: "100%",
      }}
    >
      {!sidebarCollapsed && (
        <TaxInfoSidebar
          sections={contentSections}
          onNavigate={scrollToSection}
          onDocumentSelect={setSelectedDoc}
        />
      )}
      {renderBody()}
    </Box>
  );
};

export default TaxInfo;
